/**
 * Premium FNA Report View — Professional financial planning document
 * Designed for both on-screen viewing and high-quality PDF export.
 */

import { useRef } from 'react';
import { generateInsights } from '../../../lib/agents/premiumReportGenerator';
import { Button } from '../../../components/ui/button';
import { Download, ArrowLeft } from 'lucide-react';
import './FNAReportView.css';

// ─── Small helpers ───────────────────────────────────────────────────────────

function fmt(n) {
  return '$' + Math.round(n || 0).toLocaleString();
}

function pct(current, total) {
  if (!total || total === 0) return 100;
  return Math.min((current / total) * 100, 100);
}

function progressClass(p) {
  if (p >= 100) return 'p-full';
  if (p >= 80) return 'p-good';
  if (p >= 50) return 'p-warn';
  return 'p-low';
}

function statusBadge(remaining) {
  if (!remaining || remaining <= 0) return <span className="fna-badge fna-badge-funded">Funded</span>;
  return <span className="fna-badge fna-badge-gap">Gap</span>;
}

function ProgressBar({ value, total, label }) {
  const p = pct(value, total);
  return (
    <div className="fna-progress-wrap">
      <div className="fna-progress-head">
        <span className="fna-progress-lbl">{label}</span>
        <span className="fna-progress-pct">{p.toFixed(0)}%</span>
      </div>
      <div className="fna-progress-track">
        <div className={`fna-progress-fill ${progressClass(p)}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

function MetricCard({ label, value, status = 'neutral', sub }) {
  return (
    <div className={`fna-metric-card ${status}`}>
      <div className="fna-metric-label">{label}</div>
      <div className={`fna-metric-value ${status}`}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SectionHeader({ num, title, subtitle }) {
  return (
    <div className="fna-section-header">
      <span className="fna-section-num">{num}</span>
      <div className="fna-section-text">
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
  );
}

function IntroBanner({ icon, title, body }) {
  return (
    <div className="fna-intro-banner">
      <div className="fna-intro-icon">{icon}</div>
      <div className="fna-intro-content">
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
    </div>
  );
}

function InsightBox({ text }) {
  if (!text) return null;
  return (
    <div className="fna-insight">
      <p>{text}</p>
    </div>
  );
}

// ─── SVG icons (stroke-based, PDF-safe) ──────────────────────────────────────

const IconShield = (
  <svg viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L3 7v5c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V7L12 2z" />
  </svg>
);

const IconBriefcase = (
  <svg viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
  </svg>
);

const IconHeart = (
  <svg viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
  </svg>
);

const IconTrendingUp = (
  <svg viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const IconGradCap = (
  <svg viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 22 8.5 12 15 2 8.5 12 2" />
    <line x1="12" y1="15" x2="12" y2="22" />
    <path d="M20 11v5a8 8 0 0 1-16 0v-5" />
  </svg>
);

const IconAlertCircle = (
  <svg viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const IconTarget = (
  <svg viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

// ─── Main component ───────────────────────────────────────────────────────────

export default function FNAReportView({ report, onBack, onDownload, backLabel = 'Back to Form' }) {
  const reportRef = useRef(null);

  if (!report) {
    return (
      <div className="p-6">
        <p>No report data available.</p>
        <Button onClick={onBack} className="mt-4">{backLabel}</Button>
      </div>
    );
  }

  const calc = report.calculations || {};
  const insights = generateInsights(report, calc);

  const client1Name = report.client1?.name || 'Client';
  const client2Name = report.client2?.name || '';
  const clientLabel = client2Name ? `${client1Name} & ${client2Name}` : client1Name;

  const reportDate = new Date(report.generatedAt || Date.now()).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  // ── Top-priority logic ──────────────────────────────────────────────────────
  const discretionary = calc.cashFlow?.discretionaryIncome ?? 0;
  const survivorGap = (calc.survivor1?.remainingNeed || 0) + (calc.survivor2?.remainingNeed || 0);
  const emergencyGap = calc.emergencyFund?.remainingNeed || 0;
  const disabilityGap = ((calc.disability1?.monthlyRemaining || 0) + (calc.disability2?.monthlyRemaining || 0)) * 12;
  const retirementShortfall = calc.retirement?.shortfall || 0;
  const educationShortfall = calc.education?.shortfall || 0;

  let topTitle = '';
  let topMsg = '';
  if (discretionary < 0) {
    topTitle = 'Improve Cash Flow First';
    topMsg = `Your expenses exceed income by ${fmt(Math.abs(discretionary))} per month. Addressing this creates room to save and protect your family.`;
  } else if (survivorGap > 0) {
    topTitle = 'Life Insurance Gap';
    topMsg = `Your family has ${fmt(survivorGap)} in unmet life insurance need. Closing this gap protects their lifestyle if the unexpected happens.`;
  } else if (emergencyGap > 0) {
    topTitle = 'Emergency Fund';
    topMsg = `You're ${fmt(emergencyGap)} away from a fully funded emergency reserve — your safety net before all other goals.`;
  } else if (disabilityGap > 0) {
    topTitle = 'Disability Income Protection';
    topMsg = `There is ${fmt(disabilityGap / 12)}/month in uncovered disability need. Your ability to earn is your biggest asset — protect it.`;
  } else if (retirementShortfall > 0) {
    topTitle = 'Retirement Savings';
    topMsg = `Retirement shortfall is ${fmt(retirementShortfall)}. Increasing monthly contributions now has a compounding impact over time.`;
  } else if (educationShortfall > 0) {
    topTitle = 'Education Funding';
    topMsg = `Education funding gap is ${fmt(educationShortfall)}. Starting or increasing savings now reduces future stress.`;
  } else {
    topTitle = 'Stay on Track';
    topMsg = 'Your plan looks solid. Focus on monitoring progress and updating the analysis as life changes.';
  }

  const gapCount = [
    discretionary < 0,
    survivorGap > 0,
    emergencyGap > 0,
    disabilityGap > 0,
    retirementShortfall > 0,
    educationShortfall > 0,
  ].filter(Boolean).length;

  // ── Progress percentages ────────────────────────────────────────────────────
  const s1p = pct(
    (calc.survivor1?.existingInsurance || 0) + (calc.survivor1?.availableAssets || 0),
    calc.survivor1?.totalNeed || 0
  );
  const s2p = pct(
    (calc.survivor2?.existingInsurance || 0) + (calc.survivor2?.availableAssets || 0),
    calc.survivor2?.totalNeed || 0
  );
  const efp = pct(calc.emergencyFund?.currentEmergencyFund || 0, calc.emergencyFund?.totalNeed || 0);
  const retp = pct(calc.retirement?.projectedAssets || 0, calc.retirement?.lumpSumNeeded || 0);
  const edp = pct(calc.education?.currentEducationSavings || 0, calc.education?.totalLumpSumNeeded || 0);

  // ── Assets / debts totals ───────────────────────────────────────────────────
  const totalAssets = (report.assets || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const totalDebts = (report.debts || []).reduce((s, d) => s + (Number(d.balance) || 0), 0);
  const totalDebtPayments = (report.debts || []).reduce((s, d) => s + (Number(d.monthlyPayment) || 0), 0);
  const netWorth = totalAssets - totalDebts;

  // ── Scorecard rows ──────────────────────────────────────────────────────────
  const scorecardRows = [
    {
      area: 'Life Insurance',
      total: fmt(calc.survivor1?.totalNeed || 0),
      have: fmt((calc.survivor1?.existingInsurance || 0) + (calc.survivor1?.availableAssets || 0)),
      gap: survivorGap,
      badge: survivorGap > 0 ? 'gap' : 'funded',
    },
    {
      area: 'Disability Income',
      total: fmt(((calc.disability1?.annualNeed || 0) + (calc.disability2?.annualNeed || 0))),
      have: fmt(((calc.disability1?.existingDisabilityIncome || 0) + (calc.disability2?.existingDisabilityIncome || 0))),
      gap: disabilityGap,
      badge: disabilityGap > 0 ? 'gap' : 'funded',
    },
    {
      area: 'Emergency Fund',
      total: fmt(calc.emergencyFund?.totalNeed || 0),
      have: fmt(calc.emergencyFund?.currentEmergencyFund || 0),
      gap: emergencyGap,
      badge: emergencyGap > 0 ? (efp >= 50 ? 'partial' : 'gap') : 'funded',
    },
    {
      area: 'Retirement',
      total: fmt(calc.retirement?.lumpSumNeeded || 0),
      have: fmt(calc.retirement?.projectedAssets || 0),
      gap: retirementShortfall,
      badge: retirementShortfall > 0 ? (retp >= 50 ? 'partial' : 'gap') : 'funded',
    },
    {
      area: 'Education',
      total: fmt(calc.education?.totalLumpSumNeeded || 0),
      have: fmt(calc.education?.currentEducationSavings || 0),
      gap: educationShortfall,
      badge: educationShortfall > 0 ? (edp >= 50 ? 'partial' : 'gap') : 'funded',
    },
  ];

  // ── Action items ────────────────────────────────────────────────────────────
  const actionItems = [
    survivorGap > 0 && {
      title: 'Close the Life Insurance Gap',
      desc: `Your family needs ${fmt(survivorGap)} more in coverage. Speak with your advisor about term or permanent life insurance options to protect their financial future.`,
    },
    disabilityGap > 0 && {
      title: 'Secure Disability Income Protection',
      desc: `You have ${fmt(disabilityGap / 12)}/month in uncovered disability need. Your ability to earn income is your most valuable asset — a disability policy protects it.`,
    },
    emergencyGap > 0 && {
      title: 'Build Your Emergency Reserve',
      desc: `You need ${fmt(emergencyGap)} more to reach a 6-month emergency fund. Start a dedicated savings account and automate a monthly transfer to build this cushion.`,
    },
    retirementShortfall > 0 && {
      title: 'Accelerate Retirement Savings',
      desc: `An additional ${fmt(calc.retirement?.monthlySavingsNeeded || 0)}/month will close your ${fmt(retirementShortfall)} retirement shortfall. Maximize 401(k) and IRA contributions first.`,
    },
    educationShortfall > 0 && {
      title: 'Fund Education Goals',
      desc: `Save an additional ${fmt(calc.education?.monthlySavingsNeeded || 0)}/month to fully fund your children's education. A 529 plan offers tax-advantaged growth.`,
    },
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* ── Action bar ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#fff', padding:'12px 16px', borderRadius:10, border:'1px solid #e5e7eb', position:'sticky', top:0, zIndex:10, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <Button variant="outline" onClick={onBack} style={{ display:'flex', alignItems:'center', gap:6 }}>
          <ArrowLeft className="w-4 h-4" />{backLabel}
        </Button>
        <Button onClick={() => onDownload(reportRef.current)} style={{ display:'flex', alignItems:'center', gap:6, background:'#1a56db', color:'#fff' }}>
          <Download className="w-4 h-4" />Download PDF
        </Button>
      </div>

      {/* ── How to use strip ── */}
      <div className="fna-howto">
        <h3>How to Use This Report</h3>
        <ul>
          <li>① <strong>Executive Summary</strong> — your financial health at a glance</li>
          <li>② <strong>Review Cash Flow</strong> — do you have room to act on recommendations?</li>
          <li>③ <strong>Read the Action Plan</strong> — one clear next step beats a long list</li>
        </ul>
      </div>

      {/* ── Report content ── */}
      <div style={{ overflow:'auto', maxHeight:'calc(100vh - 200px)', border:'1px solid #e5e7eb', borderRadius:10 }}>
        <div
          ref={reportRef}
          className="fna-report"
          style={{ maxWidth: '210mm', margin: '0 auto', background: '#fff' }}
        >

          {/* ══ 1. COVER PAGE ══════════════════════════════════════════════════ */}
          <div className="fna-cover">
            {/* Top navy accent bar — brand color strip, no logo here */}
            <div className="fna-cover-top">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 3, height: 24, background: '#d97706', borderRadius: 2 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.06em' }}>
                  GROW AND STAY RICH
                </span>
              </div>
              <span className="fna-cover-top-label">Confidential</span>
            </div>

            {/* White body — logo centered and properly sized */}
            <div className="fna-cover-body">
              {/* Logo: gold on white, centered, prominent but not oversized */}
              <img
                src="/images/logo/logo-stacked-royalblue.svg"
                alt="Grow And Stay Rich"
                style={{
                  width: 160,
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: 28,
                  display: 'block',
                }}
              />

              <p className="fna-cover-eyebrow">Financial Planning Report</p>
              <h1 className="fna-cover-title">Financial Needs<br />Analysis</h1>
              <div className="fna-cover-accent" />
              <p className="fna-cover-for">Prepared for</p>
              <p className="fna-cover-name">{clientLabel}</p>
              <p className="fna-cover-tagline">
                This report is prepared to help you and your advisor<br />decide your next step with confidence.
              </p>
            </div>

            <div className="fna-cover-bottom">
              <div className="fna-cover-meta">
                <div>Report Date: <strong>{reportDate}</strong></div>
                <div>Prepared by <strong>Grow And Stay Rich</strong></div>
              </div>
              <div className="fna-cover-meta" style={{ textAlign: 'right' }}>
                <div>Confidential Financial Planning Document</div>
                <div>Version 1.0</div>
              </div>
            </div>
          </div>

          {/* ══ 2. TABLE OF CONTENTS ═══════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="TOC" title="Table of Contents" subtitle="Your complete financial analysis overview" />
            {[
              'Important Notes & Disclaimers',
              'Confirmation of Facts',
              'Your Life Goals & Dreams',
              'Executive Summary',
              'Cash Flow Analysis',
              'Survivor Needs Analysis',
              'Disability Income Protection',
              'Long-Term Care Planning',
              'Emergency Fund Strategy',
              'Debt Management',
              'Retirement Planning',
              'Education Funding',
              'Action Plan & Recommendations',
              'Key Assumptions',
            ].map((item, i) => (
              <div key={i} className="fna-toc-row">
                <span className="fna-toc-num">{i + 1}.</span>
                <span className="fna-toc-label">{item}</span>
              </div>
            ))}
          </div>

          {/* ══ 3. DISCLAIMERS ═════════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="01" title="Important Notes & Disclaimers" subtitle="Please read before reviewing this report" />
            <div className="fna-disclaimer">
              <p>This analysis provides broad, general guidelines to help shape your thinking about your financial needs. It serves as a guide for discussions with your professional advisors and is not a substitute for comprehensive financial planning advice.</p>
              <p>Each life goal illustrated in this analysis is calculated independently and is not intended to represent a comprehensive financial plan. Calculations are estimates based on the information you provided. Actual values, rates of growth, and tax rates may differ significantly from projections.</p>
              <p>No legal or accounting advice is being rendered. You should discuss all strategies with your legal and tax advisors before making any financial decisions.</p>
              <p>This report is <strong>confidential and intended solely for the use of the client(s) named herein</strong>. Unauthorized distribution is prohibited.</p>
            </div>
          </div>

          {/* ══ 4. CONFIRMATION OF FACTS ═══════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="02" title="Confirmation of Facts" subtitle="Personal information & current financial situation" />

            {/* Client info */}
            <div className="fna-subsec"><h3>Client Information</h3></div>
            <div className="fna-surface" style={{ marginTop: 0 }}>
              {report.client1 && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, color: '#1e3a5f' }}>{report.client1.name || 'Client 1'}</span>
                  <span style={{ color: '#6b7280', fontSize: 14 }}> · Age {report.client1.age || 'N/A'} · {report.client1.gender || 'N/A'} · Annual Salary: {fmt(report.client1.salary || 0)}</span>
                </div>
              )}
              {report.client2?.name && (
                <div>
                  <span style={{ fontWeight: 700, color: '#1e3a5f' }}>{report.client2.name}</span>
                  <span style={{ color: '#6b7280', fontSize: 14 }}> · Age {report.client2.age || 'N/A'} · {report.client2.gender || 'N/A'} · Annual Salary: {fmt(report.client2.salary || 0)}</span>
                </div>
              )}
              {report.address && <div style={{ color: '#6b7280', fontSize: 14, marginTop: 6 }}>Address: {report.address}</div>}
            </div>

            {/* Children */}
            {report.children && report.children.length > 0 && (
              <>
                <div className="fna-subsec"><h3>Children & Dependents</h3></div>
                <div className="fna-surface" style={{ marginTop: 0 }}>
                  {report.children.map((child, idx) => (
                    <div key={idx} style={{ padding: '4px 0', fontSize: 14, color: '#374151' }}>
                      <strong>{child.name || `Child ${idx + 1}`}</strong> · Age {child.currentAge ?? child.age ?? 'N/A'}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Assets */}
            {report.assets?.length > 0 && (
              <>
                <div className="fna-subsec"><h3>Assets Summary</h3></div>
                <table className="fna-table" style={{ marginTop: 0 }}>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.assets.map((a, i) => (
                      <tr key={i}>
                        <td>{a.description || 'Asset'}</td>
                        <td>{fmt(a.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td>Total Assets</td><td>{fmt(totalAssets)}</td></tr>
                  </tfoot>
                </table>
              </>
            )}

            {/* Debts */}
            {report.debts?.length > 0 && (
              <>
                <div className="fna-subsec" style={{ marginTop: 20 }}><h3>Debts Summary</h3></div>
                <table className="fna-table" style={{ marginTop: 0 }}>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Balance</th>
                      <th>Monthly Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.debts.map((d, i) => (
                      <tr key={i}>
                        <td>{d.description || 'Debt'}</td>
                        <td>{fmt(d.balance)}</td>
                        <td>{fmt(d.monthlyPayment)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total Debts</td>
                      <td>{fmt(totalDebts)}</td>
                      <td>{fmt(totalDebtPayments)}/mo</td>
                    </tr>
                  </tfoot>
                </table>
              </>
            )}

            {/* Net Worth */}
            {(totalAssets > 0 || totalDebts > 0) && (
              <div className="fna-net-worth">
                <div>
                  <div className="fna-net-worth-label">Net Worth</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>Total Assets minus Total Debts</div>
                </div>
                <div className="fna-net-worth-value" style={{ color: netWorth >= 0 ? '#4ade80' : '#f87171' }}>
                  {fmt(netWorth)}
                </div>
              </div>
            )}

            {/* Existing insurance */}
            {report.lifeInsurance?.length > 0 && (
              <>
                <div className="fna-subsec" style={{ marginTop: 24 }}><h3>Existing Life Insurance</h3></div>
                <table className="fna-table" style={{ marginTop: 0 }}>
                  <thead>
                    <tr>
                      <th>Insured</th>
                      <th>Death Benefit</th>
                      <th>Annual Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.lifeInsurance.map((p, i) => (
                      <tr key={i}>
                        <td>{p.insured || '—'}</td>
                        <td>{fmt(p.deathBenefit)}</td>
                        <td>{fmt(p.annualPremium)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* ══ 5. LIFE GOALS ══════════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="03" title="Isn't It Time You Started Dreaming Again?" subtitle="Your life goals & financial dreams" />

            <IntroBanner
              icon={IconTarget}
              title="Your Goals Shape Your Plan"
              body="Every recommendation in this report is designed to protect and fund the future you've described below."
            />

            <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.7, marginBottom: 24 }}>
              Most dreams in life require money to achieve them. We believe you shouldn't have to compromise your dreams — the right financial plan ensures your goals are reachable, affordable, and protected.
            </p>

            {report.goals ? (
              <>
                {report.goals.shortTerm?.length > 0 && (
                  <>
                    <div className="fna-subsec"><h3>Short-Term Goals <span style={{ fontWeight: 400, color: '#6b7280' }}>(1–3 years)</span></h3></div>
                    {report.goals.shortTerm.map((g, i) => (
                      <div key={i} className="fna-goal-item">
                        <span className="fna-goal-dot" />
                        <span>{g}</span>
                      </div>
                    ))}
                  </>
                )}
                {report.goals.midTerm?.length > 0 && (
                  <>
                    <div className="fna-subsec" style={{ marginTop: 20 }}><h3>Mid-Term Goals <span style={{ fontWeight: 400, color: '#6b7280' }}>(3–7 years)</span></h3></div>
                    {report.goals.midTerm.map((g, i) => (
                      <div key={i} className="fna-goal-item">
                        <span className="fna-goal-dot" style={{ background: '#d97706' }} />
                        <span>{g}</span>
                      </div>
                    ))}
                  </>
                )}
                {report.goals.longTerm?.length > 0 && (
                  <>
                    <div className="fna-subsec" style={{ marginTop: 20 }}><h3>Long-Term Goals <span style={{ fontWeight: 400, color: '#6b7280' }}>(7+ years)</span></h3></div>
                    {report.goals.longTerm.map((g, i) => (
                      <div key={i} className="fna-goal-item">
                        <span className="fna-goal-dot" style={{ background: '#059669' }} />
                        <span>{g}</span>
                      </div>
                    ))}
                  </>
                )}
              </>
            ) : (
              <p style={{ color: '#6b7280', fontSize: 14 }}>No goals recorded. Add them in the form to see personalized guidance here.</p>
            )}
          </div>

          {/* ══ 6. EXECUTIVE SUMMARY ═══════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="04" title="Executive Summary" subtitle="Your complete financial health at a glance" />

            {/* Top priority */}
            <div className="fna-priority">
              <div className="fna-priority-badge">Your Top Priority</div>
              <div className="fna-priority-title">{topTitle}</div>
              <div className="fna-priority-body">{topMsg}</div>
              {gapCount > 0 && (
                <div className="fna-priority-note">
                  You have <strong>{gapCount} {gapCount === 1 ? 'gap' : 'gaps'}</strong> identified in this report. Addressing your top priority above will have the greatest impact.
                </div>
              )}
            </div>

            {/* Overall insight */}
            <InsightBox text={insights.overall} />

            {/* Financial Health Scorecard */}
            <div className="fna-subsec" style={{ marginTop: 28 }}><h3>Financial Health Scorecard</h3></div>
            <table className="fna-scorecard">
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Total Need</th>
                  <th>Current Have</th>
                  <th>Gap</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {scorecardRows.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: '#1e3a5f' }}>{row.area}</td>
                    <td>{row.total}</td>
                    <td>{row.have}</td>
                    <td style={{ color: row.gap > 0 ? '#dc2626' : '#059669', fontWeight: 700 }}>
                      {row.gap > 0 ? fmt(row.gap) : '—'}
                    </td>
                    <td>
                      <span className={`fna-badge fna-badge-${row.badge}`}>
                        {row.badge === 'funded' ? 'Funded' : row.badge === 'partial' ? 'Partial' : 'Gap'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Key metric cards */}
            <div className="fna-subsec" style={{ marginTop: 4 }}><h3>Key Gap Amounts</h3></div>
            <div className="fna-metric-grid">
              <MetricCard label="Life Insurance Gap" value={fmt(survivorGap)} status={survivorGap > 0 ? 'gap' : 'funded'} />
              <MetricCard label="Disability Gap (annual)" value={fmt(disabilityGap) + '/yr'} status={disabilityGap > 0 ? 'gap' : 'funded'} />
              <MetricCard label="Emergency Fund Gap" value={fmt(emergencyGap)} status={emergencyGap > 0 ? 'gap' : 'funded'} />
              <MetricCard label="Retirement Shortfall" value={fmt(retirementShortfall)} status={retirementShortfall > 0 ? 'gap' : 'funded'} />
              <MetricCard label="Education Funding Gap" value={fmt(educationShortfall)} status={educationShortfall > 0 ? 'gap' : 'funded'} />
              <MetricCard label="Monthly Discretionary" value={fmt(discretionary) + '/mo'} status={discretionary >= 0 ? 'funded' : 'gap'} />
            </div>
          </div>

          {/* ══ 7. CASH FLOW ═══════════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="05" title="Cash Flow Analysis" subtitle="An important step toward financial independence is knowing where your money goes each month" />

            <div className="fna-cf-grid">
              {/* Income column */}
              <div>
                <h3 className="fna-cf-col-title">Monthly Income</h3>
                <div className="fna-cf-col-body">
                  <div className="fna-cf-row">
                    <span className="fna-cf-label">{client1Name} Salary</span>
                    <span className="fna-cf-value">{fmt((report.client1?.salary || 0) / 12)}</span>
                  </div>
                  {client2Name && (
                    <div className="fna-cf-row">
                      <span className="fna-cf-label">{client2Name} Salary</span>
                      <span className="fna-cf-value">{fmt((report.client2?.salary || 0) / 12)}</span>
                    </div>
                  )}
                  <div className="fna-cf-row">
                    <span className="fna-cf-label">Other Income</span>
                    <span className="fna-cf-value">$0</span>
                  </div>
                  <div className="fna-cf-row total">
                    <span className="fna-cf-label">Gross Monthly Income</span>
                    <span className="fna-cf-value">{fmt(calc.cashFlow?.grossIncome || 0)}</span>
                  </div>
                  <div className="fna-cf-row">
                    <span className="fna-cf-label">Est. Monthly Taxes</span>
                    <span className="fna-cf-value" style={{ color: '#dc2626' }}>− {fmt(calc.cashFlow?.taxes || 0)}</span>
                  </div>
                  <div className="fna-cf-row total">
                    <span className="fna-cf-label">Net Monthly Income</span>
                    <span className="fna-cf-value">{fmt(calc.cashFlow?.netIncome || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Expense column */}
              <div>
                <h3 className="fna-cf-col-title">Monthly Expenses</h3>
                <div className="fna-cf-col-body">
                  <div className="fna-cf-row">
                    <span className="fna-cf-label">Mortgage / Rent / Debts</span>
                    <span className="fna-cf-value">{fmt(calc.cashFlow?.breakdown?.debtPayments || 0)}</span>
                  </div>
                  <div className="fna-cf-row">
                    <span className="fna-cf-label">Savings Contributions</span>
                    <span className="fna-cf-value">{fmt(calc.cashFlow?.monthlySavings || 0)}</span>
                  </div>
                  <div className="fna-cf-row">
                    <span className="fna-cf-label">Life Insurance Premiums</span>
                    <span className="fna-cf-value">{fmt(calc.cashFlow?.breakdown?.lifeInsurance || 0)}</span>
                  </div>
                  {Array.isArray(report.expenses) && report.expenses
                    .filter(e => (Number(e.amount) || 0) > 0)
                    .map((e, i) => (
                      <div key={i} className="fna-cf-row">
                        <span className="fna-cf-label">{e.category || 'Other'}</span>
                        <span className="fna-cf-value">{fmt(e.amount)}</span>
                      </div>
                    ))}
                  <div className="fna-cf-row total">
                    <span className="fna-cf-label">Total Monthly Expenses</span>
                    <span className="fna-cf-value">{fmt(calc.cashFlow?.totalExpenses || 0)}</span>
                  </div>
                  <div className="fna-cf-row discretionary">
                    <span style={{ color: '#ffffff', fontWeight: 700 }}>Discretionary Income</span>
                    <span style={{ color: discretionary >= 0 ? '#4ade80' : '#f87171', fontWeight: 800, fontSize: 17 }}>
                      {fmt(discretionary)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <InsightBox text={insights.cashFlow} />
          </div>

          {/* ══ 8. SURVIVOR NEEDS ══════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="06" title="Survivor Needs Analysis" subtitle="Protecting your family's financial future if the unexpected happens" />

            <IntroBanner
              icon={IconShield}
              title="Protect What Matters Most"
              body="Life insurance is not just about death — it's about ensuring your family can maintain their lifestyle, pay off debts, and fund their dreams without financial stress."
            />

            {calc.survivor1 && (
              <>
                <div className="fna-subsec"><h3>{client1Name}'s Survivor Protection</h3></div>
                <div className="fna-metric-grid">
                  <MetricCard label="Total Need" value={fmt(calc.survivor1.totalNeed)} status="neutral" />
                  <MetricCard label="Existing Insurance" value={fmt(calc.survivor1.existingInsurance)} status="funded" />
                  <MetricCard label="Available Assets" value={fmt(calc.survivor1.availableAssets)} status="funded" />
                  <MetricCard
                    label="Remaining Gap"
                    value={fmt(calc.survivor1.remainingNeed)}
                    status={calc.survivor1.remainingNeed > 0 ? 'gap' : 'funded'}
                  />
                </div>
                <ProgressBar value={(calc.survivor1.existingInsurance || 0) + (calc.survivor1.availableAssets || 0)} total={calc.survivor1.totalNeed} label="Protection Coverage" />
              </>
            )}

            {calc.survivor2 && client2Name && (
              <>
                <div className="fna-subsec" style={{ marginTop: 20 }}><h3>{client2Name}'s Survivor Protection</h3></div>
                <div className="fna-metric-grid">
                  <MetricCard label="Total Need" value={fmt(calc.survivor2.totalNeed)} status="neutral" />
                  <MetricCard label="Existing Insurance" value={fmt(calc.survivor2.existingInsurance)} status="funded" />
                  <MetricCard label="Available Assets" value={fmt(calc.survivor2.availableAssets)} status="funded" />
                  <MetricCard
                    label="Remaining Gap"
                    value={fmt(calc.survivor2.remainingNeed)}
                    status={calc.survivor2.remainingNeed > 0 ? 'gap' : 'funded'}
                  />
                </div>
                <ProgressBar value={(calc.survivor2.existingInsurance || 0) + (calc.survivor2.availableAssets || 0)} total={calc.survivor2.totalNeed} label="Protection Coverage" />
              </>
            )}

            {calc.survivor1?.breakdown && (
              <>
                <div className="fna-subsec" style={{ marginTop: 20 }}><h3>Need Breakdown</h3></div>
                <table className="fna-table">
                  <thead><tr><th>Component</th><th>Amount</th></tr></thead>
                  <tbody>
                    {calc.survivor1.breakdown.debts > 0 && <tr><td>Total Debts</td><td>{fmt(calc.survivor1.breakdown.debts)}</td></tr>}
                    {calc.survivor1.breakdown.mortgage > 0 && <tr><td>Mortgage Balance</td><td>{fmt(calc.survivor1.breakdown.mortgage)}</td></tr>}
                    {calc.survivor1.breakdown.education > 0 && <tr><td>Education Funding</td><td>{fmt(calc.survivor1.breakdown.education)}</td></tr>}
                    {calc.survivor1.breakdown.emergencyFund > 0 && <tr><td>Emergency Fund</td><td>{fmt(calc.survivor1.breakdown.emergencyFund)}</td></tr>}
                    <tr><td>Final Expenses</td><td>{fmt(calc.survivor1.breakdown.finalExpenses)}</td></tr>
                    <tr><td>Income Replacement (PV)</td><td>{fmt(calc.survivor1.breakdown.incomeReplacement)}</td></tr>
                  </tbody>
                  <tfoot><tr><td>Total Survivor Need</td><td>{fmt(calc.survivor1.totalNeed)}</td></tr></tfoot>
                </table>
              </>
            )}

            <InsightBox text={insights.survivor} />
          </div>

          {/* ══ 9. DISABILITY ══════════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="07" title="Disability Income Protection" subtitle="Your ability to earn income is your most valuable financial asset" />

            <IntroBanner
              icon={IconBriefcase}
              title="Protect Your Income Stream"
              body="Disability is far more likely than death before age 65. Without income protection, a disability could force you to drain retirement savings and derail decades of planning."
            />

            {calc.disability1 && (
              <>
                <div className="fna-subsec"><h3>{client1Name}'s Disability Protection</h3></div>
                <div className="fna-metric-grid">
                  <MetricCard label="Monthly Need (60% of income)" value={fmt(calc.disability1.monthlyNeed) + '/mo'} status="neutral" />
                  <MetricCard label="Existing Coverage" value={fmt(calc.disability1.existingDisabilityIncome) + '/mo'} status="funded" />
                  <MetricCard label="Monthly Gap" value={fmt(calc.disability1.monthlyRemaining) + '/mo'} status={calc.disability1.monthlyRemaining > 0 ? 'gap' : 'funded'} />
                  <MetricCard label="Annual Gap" value={fmt((calc.disability1.monthlyRemaining || 0) * 12) + '/yr'} status={calc.disability1.monthlyRemaining > 0 ? 'gap' : 'funded'} />
                </div>
              </>
            )}

            {calc.disability2 && client2Name && (
              <>
                <div className="fna-subsec" style={{ marginTop: 20 }}><h3>{client2Name}'s Disability Protection</h3></div>
                <div className="fna-metric-grid">
                  <MetricCard label="Monthly Need (60% of income)" value={fmt(calc.disability2.monthlyNeed) + '/mo'} status="neutral" />
                  <MetricCard label="Existing Coverage" value={fmt(calc.disability2.existingDisabilityIncome) + '/mo'} status="funded" />
                  <MetricCard label="Monthly Gap" value={fmt(calc.disability2.monthlyRemaining) + '/mo'} status={calc.disability2.monthlyRemaining > 0 ? 'gap' : 'funded'} />
                  <MetricCard label="Annual Gap" value={fmt((calc.disability2.monthlyRemaining || 0) * 12) + '/yr'} status={calc.disability2.monthlyRemaining > 0 ? 'gap' : 'funded'} />
                </div>
              </>
            )}

            <InsightBox text={insights.disability} />
          </div>

          {/* ══ 10. LONG-TERM CARE ═════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="08" title="Long-Term Care Planning" subtitle="Preparing for the cost of care in your later years" />

            <IntroBanner
              icon={IconHeart}
              title="Plan for Long-Term Care Costs"
              body="70% of people over 65 will need some form of long-term care. Planning now — while you're healthy — means lower premiums and better coverage options."
            />

            {calc.ltc1 && (
              <>
                <div className="fna-subsec"><h3>{client1Name}'s LTC Needs</h3></div>
                <div className="fna-metric-grid">
                  <MetricCard label="Monthly Need at LTC Age" value={fmt(calc.ltc1.remainingMonthlyNeed ?? calc.ltc1.remainingNeed) + '/mo'} status="neutral" />
                  <MetricCard label="3-Year Lump Sum Needed" value={fmt(calc.ltc1.lumpSumAtLTC ?? calc.ltc1.lumpSumNeeded)} status="neutral" />
                  <MetricCard label="Monthly Savings to Fund" value={fmt(calc.ltc1.monthlySavingsNeeded) + '/mo'} status="warn" />
                  <MetricCard label="Existing LTC Benefit" value={fmt(calc.ltc1.existingLTCBenefit) + '/mo'} status="funded" />
                </div>
              </>
            )}

            {calc.ltc2 && client2Name && (
              <>
                <div className="fna-subsec" style={{ marginTop: 20 }}><h3>{client2Name}'s LTC Needs</h3></div>
                <div className="fna-metric-grid">
                  <MetricCard label="Monthly Need at LTC Age" value={fmt(calc.ltc2.remainingMonthlyNeed ?? calc.ltc2.remainingNeed) + '/mo'} status="neutral" />
                  <MetricCard label="3-Year Lump Sum Needed" value={fmt(calc.ltc2.lumpSumAtLTC ?? calc.ltc2.lumpSumNeeded)} status="neutral" />
                  <MetricCard label="Monthly Savings to Fund" value={fmt(calc.ltc2.monthlySavingsNeeded) + '/mo'} status="warn" />
                  <MetricCard label="Existing LTC Benefit" value={fmt(calc.ltc2.existingLTCBenefit) + '/mo'} status="funded" />
                </div>
              </>
            )}

            <InsightBox text={insights.ltc} />
          </div>

          {/* ══ 11. EMERGENCY FUND ═════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="09" title="Emergency Fund Strategy" subtitle="Your financial safety net against the unexpected" />

            <IntroBanner
              icon={IconAlertCircle}
              title="Build Your Safety Net"
              body="An emergency fund prevents you from going into debt during unexpected events and gives you the confidence to make bold financial decisions without fear."
            />

            <div className="fna-metric-grid fna-metric-grid-3">
              <MetricCard label="Emergency Fund Goal" value={fmt(calc.emergencyFund?.totalNeed)} status="neutral" sub="6 months of expenses" />
              <MetricCard label="Current Balance" value={fmt(calc.emergencyFund?.currentEmergencyFund)} status="funded" />
              <MetricCard
                label="Remaining Gap"
                value={fmt(calc.emergencyFund?.remainingNeed)}
                status={calc.emergencyFund?.remainingNeed > 0 ? 'gap' : 'funded'}
              />
            </div>

            <ProgressBar
              value={calc.emergencyFund?.currentEmergencyFund || 0}
              total={calc.emergencyFund?.totalNeed || 0}
              label="Emergency Fund Progress"
            />

            <InsightBox text={insights.emergencyFund} />
          </div>

          {/* ══ 12. DEBT MANAGEMENT ════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="10" title="Debt Management" subtitle="Taking control of your debt frees cash flow for wealth-building" />

            <div className="fna-metric-grid fna-metric-grid-3">
              <MetricCard
                label="Total Debt Balance"
                value={fmt(calc.debtManagement?.totalDebt)}
                status="neutral"
              />
              <MetricCard
                label="Monthly Debt Payments"
                value={fmt(calc.debtManagement?.totalMonthlyPayments) + '/mo'}
                status="neutral"
              />
              <MetricCard
                label="Debt-to-Income Ratio"
                value={(calc.debtManagement?.debtToIncomeRatio || 0).toFixed(1) + '%'}
                status={
                  (calc.debtManagement?.debtToIncomeRatio || 0) > 20 ? 'gap' :
                  (calc.debtManagement?.debtToIncomeRatio || 0) > 10 ? 'warn' : 'funded'
                }
                sub="Recommended: under 20%"
              />
            </div>

            <ProgressBar
              value={Math.max(0, 30 - (calc.debtManagement?.debtToIncomeRatio || 0))}
              total={30}
              label={`Ratio Status: ${(calc.debtManagement?.debtToIncomeRatio || 0) > 20 ? 'High — needs attention' : (calc.debtManagement?.debtToIncomeRatio || 0) > 10 ? 'Moderate' : 'Healthy'}`}
            />

            {calc.debtManagement?.debtDetails?.length > 0 && (
              <>
                <div className="fna-subsec" style={{ marginTop: 20 }}><h3>Debt Payoff Timeline</h3></div>
                <table className="fna-table">
                  <thead>
                    <tr>
                      <th>Debt</th>
                      <th>Balance</th>
                      <th>Monthly</th>
                      <th>Est. Payoff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calc.debtManagement.debtDetails.map((d, i) => (
                      <tr key={i}>
                        <td>{d.description || d.name || 'Debt'}</td>
                        <td>{fmt(d.balance)}</td>
                        <td>{fmt(d.monthlyPayment)}/mo</td>
                        <td>{d.yearsUntilPaidOff ? `${d.yearsUntilPaidOff.toFixed(1)} yrs` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <InsightBox text={insights.debtManagement} />
          </div>

          {/* ══ 13. RETIREMENT ═════════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="11" title="Retirement Planning" subtitle="Securing the lifestyle you've worked your whole life to build" />

            <IntroBanner
              icon={IconTrendingUp}
              title="Build Wealth for Your Golden Years"
              body="Every month you invest compounds into a bigger future. Small increases in savings today have a dramatic impact on retirement lifestyle — start now, not later."
            />

            <div className="fna-metric-grid">
              <MetricCard label="Lump Sum Needed at Retirement" value={fmt(calc.retirement?.lumpSumNeeded)} status="neutral" />
              <MetricCard label="Projected Assets at Retirement" value={fmt(calc.retirement?.projectedAssets)} status="funded" />
              <MetricCard
                label="Retirement Shortfall"
                value={fmt(calc.retirement?.shortfall)}
                status={calc.retirement?.shortfall > 0 ? 'gap' : 'funded'}
              />
              <MetricCard
                label="Additional Monthly Savings Needed"
                value={fmt(calc.retirement?.monthlySavingsNeeded) + '/mo'}
                status={calc.retirement?.monthlySavingsNeeded > 0 ? 'warn' : 'funded'}
              />
            </div>

            <ProgressBar
              value={calc.retirement?.projectedAssets || 0}
              total={calc.retirement?.lumpSumNeeded || 0}
              label="Retirement Funding Progress"
            />

            <InsightBox text={insights.retirement} />
          </div>

          {/* ══ 14. EDUCATION ══════════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="12" title="Education Funding" subtitle="Investing in your children's future — free from student loan debt" />

            <IntroBanner
              icon={IconGradCap}
              title="Give Your Children a Head Start"
              body="Education costs rise ~6% annually. Starting early and using tax-advantaged accounts (like a 529) dramatically reduces what you need to save each month."
            />

            <div className="fna-metric-grid">
              <MetricCard label="Total Education Cost (today's $)" value={fmt(calc.education?.totalLumpSumNeeded)} status="neutral" />
              <MetricCard label="Current Education Savings" value={fmt(calc.education?.currentEducationSavings)} status="funded" />
              <MetricCard
                label="Funding Shortfall"
                value={fmt(calc.education?.shortfall)}
                status={calc.education?.shortfall > 0 ? 'gap' : 'funded'}
              />
              <MetricCard
                label="Monthly Savings Needed"
                value={fmt(calc.education?.monthlySavingsNeeded) + '/mo'}
                status={calc.education?.monthlySavingsNeeded > 0 ? 'warn' : 'funded'}
              />
            </div>

            <ProgressBar
              value={calc.education?.currentEducationSavings || 0}
              total={calc.education?.totalLumpSumNeeded || 0}
              label="Education Funding Progress"
            />

            {report.children?.length > 0 && calc.education?.childDetails?.length > 0 && (
              <>
                <div className="fna-subsec" style={{ marginTop: 20 }}><h3>Per-Child Education Cost</h3></div>
                <table className="fna-table">
                  <thead>
                    <tr>
                      <th>Child</th>
                      <th>Current Age</th>
                      <th>Years to College</th>
                      <th>Est. Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calc.education.childDetails.map((child, i) => (
                      <tr key={i}>
                        <td>{child.name || `Child ${i + 1}`}</td>
                        <td>{child.currentAge ?? '—'}</td>
                        <td>{Math.max(0, (child.startAge || 18) - (child.currentAge || 0))} years</td>
                        <td>{fmt(child.costToFund)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <InsightBox text={insights.education} />
          </div>

          {/* ══ 15. ACTION PLAN ════════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="13" title="Action Plan & Recommendations" subtitle="Based on your goals and current situation, here is your prioritized path forward" />

            <div className="fna-adviser-note">
              <div className="fna-adviser-note-label">For Your Advisor</div>
              <p>Use this report to guide the conversation: start with the Executive Summary and "Your Top Priority," then walk through Cash Flow, then each protection or savings gap. End with one agreed next step and a follow-up date.</p>
            </div>

            {actionItems.length > 0 ? (
              <>
                <div className="fna-subsec"><h3>Prioritized Action Steps</h3></div>
                {actionItems.map((item, i) => (
                  <div key={i} className="fna-action-item">
                    <div className="fna-action-num">{i + 1}</div>
                    <div className="fna-action-body">
                      <div className="fna-action-title">{item.title}</div>
                      <div className="fna-action-desc">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="fna-surface">
                <p style={{ color: '#374151', margin: 0, fontSize: 15 }}>
                  <strong>Outstanding!</strong> Your financial plan is well-structured across all areas. Continue monitoring progress and updating your analysis as life changes.
                </p>
              </div>
            )}

            {/* Next steps */}
            <div className="fna-subsec" style={{ marginTop: 24 }}><h3>Your Next Steps</h3></div>
            <div className="fna-surface">
              <p style={{ fontSize: 15, fontWeight: 600, color: '#1e3a5f', marginBottom: 12 }}>
                Your first step: Schedule a short call with your advisor to pick one priority and set a completion date.
              </p>
              <ol style={{ margin: 0, paddingLeft: 20, color: '#374151', fontSize: 14, lineHeight: 2 }}>
                <li>Schedule a call with your advisor to review this report and choose your first action.</li>
                <li>Discuss implementation (insurance quotes, savings automation) for that priority.</li>
                <li>Set a follow-up date to track progress and update the plan as life changes.</li>
                <li>After completing the first step, move to the next priority — one action at a time builds lasting change.</li>
              </ol>
            </div>

            {/* Commitment block */}
            <div className="fna-commitment">
              <div className="fna-commitment-title">My Financial Commitment</div>
              <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
                Complete this section with your advisor to create accountability for your first action.
              </p>
              <p style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
                I commit to taking the following action:
              </p>
              <span className="fna-sig-line" />
              <div className="fna-sig-row" style={{ marginTop: 20 }}>
                <div className="fna-sig-field">
                  <label>Client Signature</label>
                  <span className="fna-sig-line" />
                </div>
                <div className="fna-sig-field" style={{ maxWidth: 160 }}>
                  <label>Target Date</label>
                  <span className="fna-sig-line" />
                </div>
                <div className="fna-sig-field">
                  <label>Advisor Signature</label>
                  <span className="fna-sig-line" />
                </div>
              </div>
            </div>
          </div>

          {/* ══ 16. KEY ASSUMPTIONS ════════════════════════════════════════════ */}
          <div className="fna-page fna-page-break">
            <SectionHeader num="14" title="Key Assumptions" subtitle="This analysis uses the following inputs. Actual results may vary." />
            <div className="fna-disclaimer">
              {report.assumptions ? (
                <>
                  <p><strong>Tax rate:</strong> {report.assumptions.taxRate ?? 20}% — used for cash flow and net income calculations.</p>
                  <p><strong>Inflation:</strong> General {report.assumptions.inflationRate ?? 3}%; education {report.assumptions.educationInflation ?? 6}%; LTC {report.assumptions.ltcInflation ?? 3}%.</p>
                  <p><strong>Investment return:</strong> Survivor/income replacement {report.assumptions.survivorReturn ?? 5}%; retirement growth {report.assumptions.retirementGrowth ?? 8}%; education savings {report.assumptions.returnRate ?? 5}%; LTC {report.assumptions.ltcReturn ?? 4}%.</p>
                  <p><strong>Survivor:</strong> Income replacement {report.assumptions.survivorIncomePctWithKids ?? 70}% while children at home, {report.assumptions.survivorIncomePctAfterKids ?? 60}% after. Final expenses {fmt(report.assumptions.finalExpenses ?? 10000)}. Life expectancy {report.assumptions.lifeExpectancy ?? 81} years.</p>
                  <p><strong>Emergency fund:</strong> {report.assumptions.emergencyMonths ?? 3} months of expenses used for goal calculation.</p>
                  <p><strong>Education:</strong> {fmt(report.assumptions.educationCost ?? 50000)} annual cost in today's dollars; {report.assumptions.educationYears ?? 4}-year college; {report.assumptions.educationPercentFund ?? 100}% to fund.</p>
                  <p><strong>Retirement:</strong> {report.assumptions.retirementYears ?? 20} years in retirement; Social Security {fmt(report.assumptions.socialSecurity ?? 0)}/year.</p>
                  <p><strong>Disability:</strong> {report.assumptions.disabilityPct ?? 60}% income replacement rate.</p>
                  <p><strong>LTC:</strong> Begins at age {report.assumptions.ltcAge ?? 75}; {fmt(report.assumptions.ltcCost ?? 6000)}/month in today's dollars; {report.assumptions.ltcDuration ?? 3}-year duration.</p>
                </>
              ) : (
                <>
                  <p><strong>Inflation:</strong> 3% per year general; ~6% for education; 3% for LTC and income replacement projections.</p>
                  <p><strong>Investment return:</strong> 5% for survivor/income replacement; 8% for retirement growth; 4% for LTC savings.</p>
                  <p><strong>Tax rate:</strong> Based on your reported information; used for net income and cash flow calculations.</p>
                  <p><strong>Life expectancy:</strong> 81 years — used for survivor needs and retirement duration.</p>
                  <p><strong>Emergency fund:</strong> 6 months of household expenses.</p>
                  <p><strong>Education:</strong> Costs assumed to grow ~6% annually; 4-year college at $50,000/year in today's dollars.</p>
                  <p><strong>LTC:</strong> Monthly need at age 75, inflated from today's estimate; 3-year duration for lump-sum calculation.</p>
                </>
              )}
            </div>
          </div>

          {/* ══ 17. CLOSING PAGE ═══════════════════════════════════════════════ */}
          <div className="fna-report-footer">
            <h2>GrowAndStayRich</h2>
            <p>Empowering Financial Success — One Family at a Time</p>
            <hr className="fna-report-footer-divider" />
            <div className="fna-report-footer-meta">
              <p>Report Generated: {reportDate} · Version 1.0</p>
              <p>Prepared for: {clientLabel}</p>
              <p>Prepared by: GrowAndStayRich Financial Planning Tools</p>
              <p style={{ marginTop: 8 }}>Confidential — For the sole use of the named client(s). Unauthorized distribution is prohibited.</p>
              <p style={{ marginTop: 4 }}>This report does not constitute legal, tax, or investment advice. Consult qualified professionals before taking action.</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
